import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Article 2 - 春季 Omakase
 * SEO 由 ArticlePage.jsx 集中管理，此組件只負責內容渲染
 */
export default function Article2() {
  return (
    <div>
      {/* 引言 */}
      <section className="mb-12">
        <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
          在日本料理的世界中，「旬」（しゅん，Shun）是一個核心概念，意指食材最美味、最具營養價值的時期。春季的 Omakase，正是將這個概念發揮到極致的最佳展現。當櫻花綻放，大地甦醒，海洋與山林都獻上最鮮美的禮物，廚師們用這些季節限定的食材，在餐盤上譜寫春天的樂章。
        </p>
      </section>

      {/* 春季 Omakase 的文化哲學 */}
      <section className="mb-16">
        <h2 id="spring-philosophy" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-pink-500">
          春季 Omakase 的文化哲學
        </h2>

        <div className="bg-gradient-to-br from-pink-50 to-rose-50 dark:from-gray-800 dark:to-gray-700 p-8 rounded-2xl mb-8">
          <h3 id="shun-concept" className="text-2xl font-bold text-gray-900 dark:text-white mb-4">「旬」的精神</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            日本料理對季節的敏感度，源自於對自然的深刻尊重。春季 Omakase 不僅是品嚐美食，更是體驗日本文化中對季節變化的細膩感知。
          </p>
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            {[
              { icon: '🌸', title: '視覺美學', desc: '櫻花、新綠等春季元素融入擺盤，讓每道菜都是藝術品' },
              { icon: '🍃', title: '味覺層次', desc: '清淡、鮮甜的春季食材，呈現細緻優雅的風味' },
              { icon: '🌱', title: '生命力量', desc: '春季食材富含營養，象徵新生與活力' },
            ].map(item => (
              <div key={item.title} className="bg-white dark:bg-gray-800 p-4 rounded-xl">
                <div className="text-3xl mb-2">{item.icon}</div>
                <h4 className="font-bold text-gray-900 dark:text-white mb-2">{item.title}</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <h3 id="seasonal-transition" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 mt-10">季節轉換的料理哲學</h3>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
          <ul className="space-y-4 text-gray-700 dark:text-gray-300">
            {[
              { period: '初春（3月）', desc: '仍帶有冬季的厚重感，但開始出現春季的清新元素' },
              { period: '仲春（4月）', desc: '春季特色最為鮮明，櫻花盛開，食材最豐富' },
              { period: '晚春（5月）', desc: '開始過渡到初夏，味道變得更加清爽明快' },
            ].map(item => (
              <li key={item.period} className="flex items-start">
                <span className="text-pink-500 mr-3 mt-1 text-xl">▸</span>
                <div><strong className="text-gray-900 dark:text-white">{item.period}</strong>：{item.desc}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 春季代表食材 */}
      <section className="mb-16">
        <h2 id="spring-ingredients" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-pink-500">
          春季代表食材深度解析
        </h2>

        <div className="space-y-8">
          {[
            {
              id: 'sakura-dai', icon: '🐟', name: '櫻鯛（桜鯛）',
              gradient: 'from-pink-500 to-rose-500',
              info: [
                { label: '最佳時期', value: '3月中旬～5月初' },
                { label: '產地', value: '瀨戶內海、明石海峽' },
                { label: '特色', value: '肉質緊實、脂肪適中' },
                { label: '名稱由來', value: '產卵前體色呈現櫻花般的粉紅色' },
              ],
              cooking: ['生魚片（刺身）：展現最純粹的鮮甜', '昆布締：增添鮮味層次', '鹽燒：鎖住水分，突顯魚肉香氣', '握壽司：最經典的呈現方式'],
              tip: { color: 'pink', text: '品嚐重點：', desc: '櫻鯛的美味在於其平衡的脂肪含量和緊實的肉質。品嚐時應注意魚肉的甜味、適度的脂肪香氣，以及在口中化開的細緻口感。最佳溫度約為室溫，過冷會掩蓋其細膩風味。' },
            },
            {
              id: 'takenoko', icon: '🎋', name: '竹筍（筍）',
              gradient: 'from-green-500 to-emerald-500',
              info: [
                { label: '最佳時期', value: '3月～5月' },
                { label: '產地', value: '京都、福岡' },
                { label: '品種', value: '孟宗竹、淡竹' },
                { label: '特色', value: '清脆爽口、帶有獨特香氣' },
              ],
              cooking: ['煮物：最能展現竹筍原味', '炭火燒烤：帶出煙燻香氣', '天婦羅：酥脆外皮與嫩筍對比', '竹筍飯：春季限定經典'],
              tip: { color: 'green', text: '新鮮度判斷：', desc: '竹筍的新鮮度極為重要，「朝掘り」（早晨採收）的竹筍最為珍貴。新鮮竹筍應該沒有苦味，質地細嫩，帶有淡淡的甜味和獨特的土壤芬芳。' },
            },
            {
              id: 'sakura-ebi', icon: '🦐', name: '櫻花蝦（桜えび）',
              gradient: 'from-red-500 to-orange-500',
              info: [
                { label: '最佳時期', value: '3月下旬～6月初' },
                { label: '產地', value: '靜岡縣駿河灣（獨家）' },
                { label: '特色', value: '鮮豔的粉紅色、濃郁鮮味' },
                { label: '稀有性', value: '全球僅駿河灣可捕撈' },
              ],
              cooking: ['生食（生櫻花蝦）：最高級享受', '天婦羅：呈現酥脆口感', '釜飯：增添香氣和色澤', '乾燥櫻花蝦：濃縮鮮味'],
              tip: { color: 'red', text: '珍貴之處：', desc: '櫻花蝦是春季最珍貴的食材之一。生櫻花蝦只有在捕撈當天才能品嚐，其甜味和獨特的口感令人難忘。' },
            },
          ].map(item => (
            <div key={item.id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
              <div className={`bg-gradient-to-r ${item.gradient} p-6`}>
                <h3 id={item.id} className="text-2xl font-bold text-white flex items-center gap-3">
                  <span className="text-3xl">{item.icon}</span>{item.name}
                </h3>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">基本資訊</h4>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                      {item.info.map(i => <li key={i.label}><strong>{i.label}</strong>：{i.value}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 dark:text-white mb-3">料理方式</h4>
                    <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                      {item.cooking.map(c => <li key={c}>• {c}</li>)}
                    </ul>
                  </div>
                </div>
                <div className={`mt-6 p-4 bg-${item.tip.color}-50 dark:bg-${item.tip.color}-900/20 rounded-xl`}>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    <strong className={`text-${item.tip.color}-600 dark:text-${item.tip.color}-400`}>{item.tip.text}</strong>{item.tip.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 春季料理時間軸 */}
      <section className="mb-16">
        <h2 id="seasonal-timeline" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-pink-500">
          春季料理時間軸
        </h2>
        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-pink-300 via-rose-300 to-red-300 dark:from-pink-700 dark:via-rose-700 dark:to-red-700"></div>
          {[
            { month: '3月', title: '初春の息吹', color: 'pink', items: ['文蛤（はまぐり）', '白魚（しらうお）', '蜆（しじみ）', '蕗薹（ふきのとう）'], desc: '早春食材帶有微微的苦味，這是「春之苦味」，被認為能喚醒冬眠的身體。' },
            { month: '4月', title: '桜の季節', color: 'rose', items: ['櫻鯛（桜鯛）', '竹筍（筍）', '櫻花蝦（桜えび）', '鰹魚（初鰹）'], desc: '櫻花盛開的季節，料理也呈現粉紅色系，視覺與味覺都達到巔峰。' },
            { month: '5月', title: '初夏への移ろい', color: 'red', items: ['蠶豆（そら豆）', '鯵魚（鯵）', '鰹魚（戻り鰹）', '新茶'], desc: '晚春時節，食材的味道變得更加清爽明快，開始出現初夏的氣息。' },
          ].map((period, idx) => (
            <div key={period.month} className={`relative mb-12 ml-16 ${idx === 2 ? 'mb-0' : ''}`}>
              <div className={`absolute -left-[2.3rem] top-2 w-6 h-6 bg-${period.color}-500 rounded-full border-4 border-white dark:border-gray-900`}></div>
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{period.month} - {period.title}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className={`font-bold text-${period.color}-600 dark:text-${period.color}-400 mb-2`}>主要食材</h4>
                    <ul className="space-y-1 text-gray-700 dark:text-gray-300 text-sm">
                      {period.items.map(i => <li key={i}>• {i}</li>)}
                    </ul>
                  </div>
                  <div>
                    <h4 className={`font-bold text-${period.color}-600 dark:text-${period.color}-400 mb-2`}>料理特色</h4>
                    <p className="text-gray-700 dark:text-gray-300 text-sm">{period.desc}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 花見文化 */}
      <section className="mb-16">
        <h2 id="hanami-culture" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-pink-500">
          花見文化與春季料理
        </h2>
        <div className="bg-gradient-to-br from-pink-50 via-rose-50 to-red-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 rounded-2xl">
          <h3 id="hanami-tradition" className="text-2xl font-bold text-gray-900 dark:text-white mb-4">花見的歷史傳統</h3>
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            花見（賞櫻）是日本春季最重要的文化活動，起源於奈良時代的貴族文化。春季 Omakase 與花見文化有著深厚的連結，體現日本文化中對短暫美好事物的珍惜與感嘆——「物哀」美學的極致展現。
          </p>
        </div>
      </section>

      {/* 結語 */}
      <section className="mb-16">
        <h2 id="conclusion" className="text-3xl font-bold text-gray-900 dark:text-white mb-6">結語</h2>
        <div className="bg-gradient-to-br from-pink-50 via-rose-50 to-red-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 rounded-2xl">
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            春季 Omakase 是一場視覺與味覺的雙重饗宴，它不僅展現了日本料理對季節的敏感度，更體現了對自然的尊重與感恩。每一道春季料理都訴說著大地甦醒的故事，從櫻鯛的粉紅色澤到竹筍的清新香氣，都是春天獨有的禮物。
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            如同櫻花的短暫綻放，春季食材的最佳賞味期也稍縱即逝。把握春天，品嚐這季節限定的美味，是對生命循環的深刻體會。
          </p>
        </div>
      </section>

      {/* 延伸閱讀 */}
      <section className="mt-16 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">延伸閱讀</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { to: '/beginners-complete-guide-to-omakase', icon: '📚', title: '初學者的 Omakase 完整指南', desc: '從零開始了解 Omakase 的基礎知識', gradient: 'from-orange-400 to-rose-500', hover: 'orange' },
            { to: '/premium-ingredients-analysis-from-tsukiji', icon: '🐟', title: '頂級食材解析', desc: '深入了解 Omakase 使用的頂級食材', gradient: 'from-blue-400 to-cyan-500', hover: 'blue' },
            { to: '/sushi-vs-kaiseki-omakase-comparison', icon: '🍱', title: '壽司 vs 懷石 Omakase', desc: '比較兩種不同風格的 Omakase', gradient: 'from-purple-400 to-pink-500', hover: 'purple' },
          ].map(link => (
            <Link key={link.to} to={link.to} className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className={`h-48 bg-gradient-to-br ${link.gradient} flex items-center justify-center`}>
                <span className="text-6xl">{link.icon}</span>
              </div>
              <div className="p-6">
                <h3 className={`text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-${link.hover}-500 transition-colors`}>{link.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{link.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}